// Source de pixels Viv pour les images découpées par le serveur (functions/tiler.py) : une image JPEG 8 bits par
// tuile, {canal}/{niveau}/{x}_{y}.jpg. Chaque tuile est une URL distincte : téléchargements en parallèle, gardés en
// cache par le navigateur, et décodés par lui (createImageBitmap) hors du fil principal. Même interface que les
// sources OME-TIFF de Viv (getTile / getRaster / shape / labels), donc même rendu : LUT, contraste, superposition.

// Valeur que Viv attend d'une tuile abandonnée (zoom ou déplacement avant la fin du chargement).
const SIGNAL_ABORTED = '__vivSignalAborted';
const LABELS = ['t', 'c', 'z', 'y', 'x'];
const RGB_LABELS = ['t', 'c', 'z', 'y', 'x', '_c'];  // dernière dimension = composantes (convention Viv)

let canvas = null;

// Décode une tuile JPEG : niveaux de gris (canal rouge, identique aux deux autres) ou couleur (RGBA, que Viv
// affiche tel quel pour les images entrelacées).
async function decode(blob, rgb) {
  const bitmap = await createImageBitmap(blob, { colorSpaceConversion: 'none', premultiplyAlpha: 'none' });
  const { width, height } = bitmap;
  if (!canvas) canvas = new OffscreenCanvas(width, height);
  if (canvas.width < width || canvas.height < height) {
    canvas.width = Math.max(canvas.width, width);
    canvas.height = Math.max(canvas.height, height);
  }
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const rgba = ctx.getImageData(0, 0, width, height).data;
  if (rgb) return { data: new Uint8Array(rgba.buffer), width, height };
  const data = new Uint8Array(width * height);
  for (let i = 0, j = 0; i < data.length; i += 1, j += 4) data[i] = rgba[j];
  return { data, width, height };
}

class JpegTileSource {
  constructor(tiles, level, urlFor) {
    const { width, height } = tiles.levels[level];
    this.rgb = !!tiles.rgb;
    this.shape = this.rgb ? [1, 1, 1, height, width, 3] : [1, tiles.channels, 1, height, width];
    this.labels = this.rgb ? RGB_LABELS : LABELS;
    this.dtype = 'Uint8';
    this.tileSize = tiles.tileSize;
    this.meta = this.rgb ? { photometricInterpretation: 2 } : {};  // 2 = RGB
    this.level = level;
    this.urlFor = urlFor;
  }

  async getTile({ x, y, selection, signal }) {
    try {
      const res = await fetch(this.urlFor(selection?.c || 0, this.level, x, y), { signal });
      if (!res.ok) throw new Error(`tile ${this.level}/${x}_${y}: HTTP ${res.status}`);
      return await decode(await res.blob(), this.rgb);
    } catch (err) {
      if (signal?.aborted) throw SIGNAL_ABORTED;
      throw err;
    }
  }

  // Image entière du niveau en niveaux de gris (plus petit niveau, une seule tuile, pour l'histogramme).
  async getRaster({ selection, signal }) {
    const [height, width] = this.shape.slice(-2);
    const nx = Math.ceil(width / this.tileSize);
    const ny = Math.ceil(height / this.tileSize);
    const data = new Uint8Array(width * height);
    const jobs = [];
    for (let y = 0; y < ny; y += 1) {
      for (let x = 0; x < nx; x += 1) {
        jobs.push(this.getTile({ x, y, selection, signal }).then(tile => {
          for (let row = 0; row < tile.height; row += 1) {
            const start = (y * this.tileSize + row) * width + x * this.tileSize;
            data.set(tile.data.subarray(row * tile.width, (row + 1) * tile.width), start);
          }
        }));
      }
    }
    await Promise.all(jobs);
    return { data, width, height };
  }

  onTileError(err) {
    console.error(err);
  }
}

// Pyramide Viv (un élément par niveau) pour la description tiles d'une image ; urlFor(c, niveau, x, y) -> URL.
export const loadTiles = (tiles, urlFor) => tiles.levels.map((_, level) => new JpegTileSource(tiles, level, urlFor));
